import { Award, Clock, Heart, Sparkles, Star, Users, ShoppingBag, MoreVertical, X, ChevronRight } from "lucide-react";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { trackEvent } from "../lib/analytics";
import AddOnsSection from "../components/AddOnsSection";
import CelebritiesSection from "../components/CelebritiesSection";
import ContactSection from "../components/ContactSection";
import FloralJewellerySection from "../components/FloralJewellerySection";
import Footer from "../components/Footer";
import GallerySection from "../components/GallerySection";
import PackagesSection from "../components/PackagesSection";
import ProductsSection from "../components/ProductsSection";
import TestimonialsSection from "../components/TestimonialsSection";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";

const HomePage = () => {
  const [activeSection, setActiveSection] = useState("hero");
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      const sections = [
        "hero",
        "about",
        "services",
        "packages",
        "celebrities",
        "addons",
        "gallery",
        "testimonials",
      ];
      const scrollPosition = window.scrollY + 200;

      for (const section of sections) {
        const element = document.getElementById(section);
        if (element) {
          const { offsetTop, offsetHeight } = element;
          if (
            scrollPosition >= offsetTop &&
            scrollPosition < offsetTop + offsetHeight
          ) {
            if (activeSection !== section) {
              setActiveSection(section);
              // Track section view in GA
              if (window.gtag) {
                window.gtag("event", "section_view", {
                  section_id: section,
                  page_location: window.location.href,
                  page_title: document.title,
                });
              }
            }
            break;
          }
        }
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      // Track navigation click in GA
      if (window.gtag) {
        window.gtag("event", "navigation_click", {
          destination_section: sectionId,
          method: "smooth_scroll",
        });
      }
      element.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md border-b border-stone-200 transition-all duration-300">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center justify-center space-x-2">
              <img
                src="./logo.png"
                style={{ height: "56px" }}
                alt="Makeup By Khushi Logo"
                className="object-contain img-lnr-none"
              />
              <div className="flex flex-col justify-center leading-tight items-start">
                <h1 className="text-xl sm:text-2xl text-center w-full font-bold text-rose-700 tracking-wide">
                  Khushi Shukla
                </h1>

                <div className="w-full h-px bg-rose-700" />

                <div className="text-xs sm:text-sm text-rose-500 tracking-wide">
                  Makeup <span className="mx-1">|</span> Hair{" "}
                  <span className="mx-1">|</span> Academy
                </div>
              </div>
            </div>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center space-x-7">
              <button
                onClick={() => scrollToSection("about")}
                className="text-lg font-medium text-stone-600 hover:text-rose-500 transition-colors"
              >
                About
              </button>
              <button
                onClick={() => scrollToSection("services")}
                className="text-lg font-medium text-stone-600 hover:text-rose-500 transition-colors"
              >
                Services
              </button>
              <button
                onClick={() => scrollToSection("packages")}
                className="text-lg font-medium text-stone-600 hover:text-rose-500 transition-colors"
              >
                Packages
              </button>
              <button
                onClick={() => scrollToSection("gallery")}
                className="text-lg font-medium text-stone-600 hover:text-rose-500 transition-colors"
              >
                Gallery
              </button>
              <Link
                to="/my-collection"
                className="text-lg font-medium text-rose-600 hover:text-rose-700 transition-colors flex items-center"
              >
                <Sparkles className="w-4 h-4 mr-1 text-rose-500" />
                Collection
              </Link>
              <button
                onClick={() => scrollToSection("contact")}
                className="text-lg font-medium text-stone-600 hover:text-rose-500 transition-colors"
              >
                Contact
              </button>
            </nav>

            {/* Header Right Actions (Book Now + 3 Dots Button for Mobile) */}
            <div className="flex items-center space-x-2">
              <Button
                onClick={() => scrollToSection("contact")}
                className="bg-rose-500 hover:bg-rose-600 text-white text-xs sm:text-sm px-3 sm:px-4 py-2 transition-all duration-300"
              >
                Book Now
              </Button>

              {/* 3 Dots Menu Button for Mobile */}
              <button
                type="button"
                onClick={() => {
                  const nextState = !isMobileMenuOpen;
                  setIsMobileMenuOpen(nextState);
                  trackEvent("mobile_menu_toggle", { state: nextState ? "open" : "close" });
                }}
                className="md:hidden p-2 rounded-lg text-stone-700 hover:text-rose-600 hover:bg-rose-50 border border-stone-200 transition-colors focus:outline-none"
                aria-label={isMobileMenuOpen ? "Close navigation menu" : "Open navigation menu"}
                aria-expanded={isMobileMenuOpen}
              >
                {isMobileMenuOpen ? (
                  <X className="w-5 h-5 text-rose-600" />
                ) : (
                  <MoreVertical className="w-5 h-5 text-stone-700" />
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Dropdown Navigation Menu */}
        {isMobileMenuOpen && (
          <div className="md:hidden border-t border-rose-100 bg-white/98 backdrop-blur-md px-4 pt-3 pb-5 shadow-xl transition-all duration-300 animate-in slide-in-from-top-2">
            <div className="flex flex-col space-y-2">
              {/* Featured Link to Collection Page */}
              <Link
                to="/my-collection"
                onClick={() => {
                  setIsMobileMenuOpen(false);
                  trackEvent("navigation_click", {
                    destination_section: "my-collection",
                    method: "mobile_menu",
                  });
                }}
                className="flex items-center justify-between p-3 rounded-xl bg-gradient-to-r from-rose-500 via-rose-600 to-rose-700 text-white shadow-md active:scale-98 transition-transform mb-1"
              >
                <div className="flex items-center space-x-2.5">
                  <div className="p-1.5 bg-white/20 rounded-lg">
                    <Sparkles className="w-4 h-4 text-amber-200 animate-pulse" />
                  </div>
                  <div>
                    <div className="font-bold text-sm">Jewellery & Collection</div>
                    <div className="text-[11px] text-rose-100">Explore 116+ pieces with WhatsApp inquiry</div>
                  </div>
                </div>
                <ChevronRight className="w-4 h-4 text-white/80" />
              </Link>

              {/* Standard Page Nav Links */}
              {[
                { name: "About Khushi", id: "about" },
                { name: "Services & Styling", id: "services" },
                { name: "Bridal Packages", id: "packages" },
                { name: "Celebrity Looks", id: "celebrities" },
                { name: "Floral Jewellery", id: "addons" },
                { name: "Portfolio Gallery", id: "gallery" },
                { name: "Client Testimonials", id: "testimonials" },
                { name: "Contact & Location", id: "contact" },
              ].map((item) => (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => {
                    setIsMobileMenuOpen(false);
                    scrollToSection(item.id);
                  }}
                  className="flex items-center justify-between w-full px-3 py-2.5 rounded-lg text-sm font-medium text-stone-700 hover:text-rose-600 hover:bg-rose-50/70 transition-colors text-left"
                >
                  <span>{item.name}</span>
                  <ChevronRight className="w-3.5 h-3.5 text-stone-400" />
                </button>
              ))}

              {/* Quick Actions at bottom of mobile menu */}
              <div className="pt-2 border-t border-stone-100 flex items-center gap-2">
                <Button
                  onClick={() => {
                    setIsMobileMenuOpen(false);
                    scrollToSection("contact");
                  }}
                  className="flex-1 bg-rose-500 hover:bg-rose-600 text-white text-xs py-2"
                >
                  Book Appointment
                </Button>
                <a
                  href="https://wa.me/919099302024?text=Hi%20Khushi!%20I%20would%20like%20to%20inquire%20about%20your%20services."
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1"
                  onClick={() => {
                    if (window.gtag) {
                      window.gtag("event", "whatsapp_click", {
                        event_category: "Engagement",
                        event_label: "Mobile Menu WhatsApp",
                      });
                    }
                  }}
                >
                  <Button
                    variant="outline"
                    className="w-full border-[#25D366] text-[#128C7E] hover:bg-[#25D366]/10 text-xs py-2"
                  >
                    WhatsApp Chat
                  </Button>
                </a>
              </div>
            </div>
          </div>
        )}
      </header>

      {/* Hero Section */}
      <section
        id="hero"
        className="relative min-h-screen flex items-center justify-center pt-20 overflow-hidden"
      >
        <div className="absolute inset-0 z-0">
          <img
            src="./header.jpg"
            alt="Professional Makeup Artist"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-transparent"></div>
          <div class="absolute bottom-0 left-0 w-full overflow-hidden leading-none">
            <svg
              class="relative block w-full h-28"
              viewBox="0 0 1440 160"
              preserveAspectRatio="none"
            >
              <path
                fill="#ffffff"
                d="M0,118 C180,70 360,70 540,118 C720,160 900,160 1080,118 C1260,70 1360,70 1440,80 L1440,160 L0,160 Z"
              />
            </svg>
          </div>
        </div>

        <div className="container mx-auto px-4 z-10 relative">
          <div className="max-w-2xl">
            <p className="text-rose-300 text-sm font-medium tracking-wider uppercase mb-4 animate-fade-in">
              Welcome To
            </p>
            <h2 className="text-5xl md:text-7xl font-bold text-white mb-6 animate-slide-up">
              Makeup By Khushi
            </h2>
            <p className="text-xl text-white mb-4 font-medium">
              Makeup Artist | Hairstylist
            </p>
            <p className="text-lg text-white mb-8 leading-relaxed">
              More than just a makeup service—it's a complete bridal experience.
              With dedication and artistry, I help brides look and feel their
              most confident, elegant selves. Makeup should enhance natural
              beauty, not mask it.
            </p>
            <div className="flex flex-wrap gap-4">
              <Button
                onClick={() => scrollToSection("packages")}
                size="lg"
                className="bg-rose-500 hover:bg-rose-600 text-white px-8 py-6 text-lg transition-all duration-300 hover:scale-105"
              >
                <Sparkles className="w-5 h-5 mr-2" />
                Get Gorgeous
              </Button>
              <Button
                onClick={() => scrollToSection("contact")}
                size="lg"
                variant="outline"
                className="border-2 border-white text-white hover:bg-white hover:text-stone-800 px-8 py-6 text-lg transition-all duration-300"
              >
                Contact Now
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section
        id="about"
        className="py-24 bg-gradient-to-b from-white to-rose-50"
      >
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <p className="text-rose-500 text-sm font-medium tracking-wider uppercase mb-2">
              Who I Am
            </p>
            <h2 className="text-4xl md:text-5xl font-serif font-bold text-stone-800 mb-4">
              About Makeup By Khushi
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-12 items-center max-w-6xl mx-auto">
            <div>
              <img
                src="https://images.unsplash.com/photo-1709477542149-f4e0e21d590b"
                alt="Professional Makeup Application"
                className="rounded-2xl shadow-2xl w-full h-[500px] object-cover"
              />
            </div>
            <div className="space-y-6">
              <p className="text-lg text-stone-600 leading-relaxed">
                MakeupbyKhushi is more than just a makeup service—it's a
                complete bridal experience. With dedication and artistry, I have
                helped countless brides look and feel their most confident,
                elegant selves. My approach is grounded in the belief that
                makeup should enhance natural beauty, not mask it.
              </p>
              <div className="bg-white p-6 rounded-xl shadow-lg border border-rose-100">
                <h3 className="text-xl font-semibold text-stone-800 mb-4">
                  What I Love Most About It
                </h3>
                <p className="text-stone-600 leading-relaxed">
                  I bring more than brushes and palettes to the table. As a
                  professional bridal stylist, hairstylist, makeup artist, and
                  outfit expert—I'm a one-stop solution for every bride. I
                  believe in working from the inside out: understanding your
                  skin, your style, your story. My process begins well before
                  the big day with skin assessments, tailored product guidance,
                  and skincare prep to ensure a flawless finish.
                </p>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div className="text-center p-4 bg-white rounded-lg shadow-md">
                  <Award className="w-8 h-8 text-rose-500 mx-auto mb-2" />
                  <p className="text-2xl font-bold text-stone-800">50+</p>
                  <p className="text-sm text-stone-600">Happy Brides</p>
                </div>
                <div className="text-center p-4 bg-white rounded-lg shadow-md">
                  <Users className="w-8 h-8 text-rose-500 mx-auto mb-2" />
                  <p className="text-2xl font-bold text-stone-800">200+</p>
                  <p className="text-sm text-stone-600">Clients Served</p>
                </div>
                <div className="text-center p-4 bg-white rounded-lg shadow-md">
                  <Clock className="w-8 h-8 text-rose-500 mx-auto mb-2" />
                  <p className="text-2xl font-bold text-stone-800">9+</p>
                  <p className="text-sm text-stone-600">Years Experience</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-24 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <p className="text-rose-500 text-sm font-medium tracking-wider uppercase mb-2">
              What I Offer
            </p>
            <h2 className="text-4xl md:text-5xl font-serif font-bold text-stone-800 mb-4">
              Services For Every Occasion
            </h2>
            <p className="text-stone-600 max-w-2xl mx-auto">
              Here are some best services for your best moments!
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-7xl mx-auto">
            <Card className="group hover:shadow-2xl transition-all duration-500 border-rose-100 hover:border-rose-300 cursor-pointer overflow-hidden">
              <div className="relative h-64 overflow-hidden">
                <img
                  src="https://images.unsplash.com/flagged/photo-1551854716-8b811be39e7e"
                  alt="Wedding Makeup"
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
              </div>
              <CardContent className="p-6">
                <Heart className="w-8 h-8 text-rose-500 mb-4" />
                <h3 className="text-xl font-semibold text-stone-800 mb-3">
                  Wedding Makeup
                </h3>
                <p className="text-stone-600">
                  From glamorous bridal looks to soft and romantic styles, trust
                  us to make you feel like a breathtaking bride.
                </p>
              </CardContent>
            </Card>

            <Card className="group hover:shadow-2xl transition-all duration-500 border-rose-100 hover:border-rose-300 cursor-pointer overflow-hidden">
              <div className="relative h-64 overflow-hidden">
                <img
                  src="https://images.unsplash.com/photo-1684868268327-7e5590bcfbd6"
                  alt="Bridesmaids Makeup"
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
              </div>
              <CardContent className="p-6">
                <Users className="w-8 h-8 text-rose-500 mb-4" />
                <h3 className="text-xl font-semibold text-stone-800 mb-3">
                  Bridesmaids Makeup
                </h3>
                <p className="text-stone-600">
                  Enhancing the natural beauty of bridesmaids, our makeup
                  service adds a touch of glamour to complement the wedding
                  ensemble.
                </p>
              </CardContent>
            </Card>

            <Card className="group hover:shadow-2xl transition-all duration-500 border-rose-100 hover:border-rose-300 cursor-pointer overflow-hidden">
              <div className="relative h-64 overflow-hidden">
                <img
                  src="https://images.unsplash.com/photo-1594465919760-441fe5908ab0"
                  alt="Baby Shower Makeup"
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
              </div>
              <CardContent className="p-6">
                <Sparkles className="w-8 h-8 text-rose-500 mb-4" />
                <h3 className="text-xl font-semibold text-stone-800 mb-3">
                  Baby Shower Makeup
                </h3>
                <p className="text-stone-600">
                  Celebrate the joyous occasion of your baby shower with our
                  makeup package, ensuring you feel beautiful and confident.
                </p>
              </CardContent>
            </Card>

            <Card className="group hover:shadow-2xl transition-all duration-500 border-rose-100 hover:border-rose-300 cursor-pointer overflow-hidden">
              <div className="relative h-64 overflow-hidden">
                <img
                  src="https://images.unsplash.com/photo-1487412947147-5cebf100ffc2"
                  alt="Photo Shoot Makeup"
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
              </div>
              <CardContent className="p-6">
                <Star className="w-8 h-8 text-rose-500 mb-4" />
                <h3 className="text-xl font-semibold text-stone-800 mb-3">
                  Photo-Shoot
                </h3>
                <p className="text-stone-600">
                  Capture your best angles with our professional photoshoot
                  makeup package, designed to make you camera-ready and flawless
                  in every shot.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Packages Section */}
      <PackagesSection scrollToSection={scrollToSection} />

      {/* Celebrities Section */}
      <CelebritiesSection />

      {/* Add-Ons Section */}
      <AddOnsSection scrollToSection={scrollToSection} />

      {/* Floral Jewellery Section */}
      <FloralJewellerySection scrollToSection={scrollToSection} />

      {/* Products Section */}
      <ProductsSection />

      {/* Gallery Section */}
      <GallerySection />

      {/* Testimonials Section */}
      <TestimonialsSection />

      {/* Contact Section */}
      <ContactSection />

      {/* Footer */}
      <Footer />

      {/* Floating WhatsApp Button */}
      <a
        href="https://wa.me/919099302024"
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-6 right-6 bg-[#25D366] text-white p-4 rounded-full shadow-2xl hover:shadow-[0_8px_30px_rgb(37,211,102,0.4)] hover:-translate-y-1 transition-all duration-300 z-50 flex items-center justify-center group"
        onClick={() => {
          if (window.gtag) {
            window.gtag("event", "whatsapp_click", {
              event_category: "Engagement",
              event_label: "Floating WhatsApp Button",
            });
          }
        }}
        aria-label="Chat on WhatsApp"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 448 512"
          fill="currentColor"
          className="w-7 h-7"
        >
          <path d="M380.9 97.1C339 55.1 283.2 32 223.9 32c-122.4 0-222 99.6-222 222 0 39.1 10.2 77.3 29.6 111L0 480l117.7-30.9c32.4 17.7 68.9 27 106.1 27h.1c122.3 0 224.1-99.6 224.1-222 0-59.3-23.1-115-65-157zM223.9 434.9c-32.9 0-65.1-8.8-93.5-25.5l-6.7-4-69.5 18.2 18.5-67.8-4.4-6.9c-18.7-29.3-28.6-63.5-28.6-99 0-101.8 82.9-184.8 184.8-184.8 49.3 0 95.7 19.2 130.6 54.1 34.8 34.9 54 81.3 54 130.7 0 101.9-83 184.8-184.8 184.8zm101.2-138.2c-5.5-2.8-32.8-16.2-37.9-18-5.1-1.9-8.8-2.8-12.5 2.8-3.7 5.6-14.3 18-17.6 21.8-3.2 3.7-6.5 4.2-12 1.4-32.6-16.3-54-29.1-75.5-66-5.7-9.8 5.7-9.1 16.3-30.3 1.8-3.7.9-6.9-.5-9.7-1.4-2.8-12.5-30.1-17.1-41.2-4.5-10.8-9.1-9.3-12.5-9.5-3.2-.2-6.9-.2-10.6-.2-3.7 0-9.7 1.4-14.8 6.9-5.1 5.6-19.4 19-19.4 46.3 0 27.3 19.9 53.7 22.6 57.4 2.8 3.7 39.1 59.7 94.8 83.8 35.2 15.2 49 16.5 66.6 13.9 10.7-1.6 32.8-13.4 37.4-26.4 4.6-13 4.6-24.1 3.2-26.4-1.3-2.5-5-3.9-10.5-6.6z" />
        </svg>
      </a>
    </div>
  );
};

export default HomePage;
